import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
    Box,
    Flex,
    Input,
    Select,
    SimpleGrid,
    Text,
    useToast,
} from '@chakra-ui/react';
import {
    DashboardPageContent,
    FiltersWrapper,
    FormWrapper,
    PageTitle,
} from 'custom-style-components';

import { EventType, People } from 'types/models';
import TOAST from 'constants/TOAST';
import { useAuth } from 'hooks/auth';
import { fetchManyOldEventsByResponsibleUser } from 'services/fetch/events';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import ErrorMessage from 'components/ErrorMessage';
import LoadingSpinner from 'components/LoadingSpinner';
import ShowParticipatingUsers from '../../components/ShowParticipatingUsers';
import { fetchManyUsersFromEvent } from 'services/fetch/users';
import EventsTable from 'components/EventTable';

const DEBOUNCE_MS = 400;

const ListOldEvents: React.FC = () => {
    const { user } = useAuth();
    const toast = useToast({
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const [eventListYear, setEventYears] = useState<number[]>();
    const [startYear, setStartYear] = useState<number>();
    
    const [oldEventList, setOldEventList] = useState<EventType[]>([]);
    const [oldEventName, setOldEventName] = useState('');
    const [debouncedOldEventName, setDebouncedOldEventName] = useState('');
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { path } = useRouteMatch();

    const [eventData, setEventData] = useState<EventType>({} as EventType);

    const [modalOpen, setModalOpen] = useState(false);

    const source = useRef(createCancelTokenSource());
    const yearDebounceRef = useRef<ReturnType<typeof setTimeout>>();

    // Debounce para o input de texto
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedOldEventName(oldEventName);
        }, DEBOUNCE_MS);
        return () => clearTimeout(handler);
    }, [oldEventName]);

    // Filtragem em tempo real utilizando useMemo
    const filteredEvents = useMemo(() => {
        if (!debouncedOldEventName) return oldEventList;
        const lower = debouncedOldEventName.toLowerCase();
        return oldEventList.filter((event) => {
            const category = event.eventCategory?.category?.toLowerCase() ?? '';
            const sigla = event.eventArea?.sigla?.toLowerCase() ?? '';
            return category.includes(lower) || sigla.includes(lower);
        });
    }, [debouncedOldEventName, oldEventList]);

    const loadEvents = useCallback(() => {
        if (!user) return;
        setLoading(true);
        source.current = createCancelTokenSource();
        fetchManyOldEventsByResponsibleUser(source.current.token, user.id, {
            limit: Number.MAX_SAFE_INTEGER,
            startYear,
        })
            .then(({ events }) => {
                setOldEventList(events);
            })
            .catch((err) => {
                setError(err.message);
                toast({
                    title: err.message,
                    status: 'error',
                });
            })
            .finally(() => setLoading(false));
    }, [user, startYear, toast]);

    useEffect(() => {
        loadEvents();
        return () => source.current.cancel();
    }, [loadEvents]);

    // Efeito para carregar unicamente a lista de anos disponíveis
    useEffect(() => {
        if (!user) return;
        const yearsSource = createCancelTokenSource();
        fetchManyOldEventsByResponsibleUser(yearsSource.token, user.id, {
            limit: Number.MAX_SAFE_INTEGER,
            startYear: undefined,
        })
            .then(({ events }) => {
                if (startYear === undefined) {
                    const years = Array.from(
                        new Set(events.map((e) => e.startDate.getFullYear()))
                    );
                    setEventYears(years);
                }
            })
            .catch((err) => {
                setError(err.message);
                toast({ title: err.message, status: 'error' });
            });

        return () => yearsSource.cancel();
    }, [user, toast, startYear]);

    const handleParticipantsClick = useCallback((event: EventType) => {
        setEventData(event);
        setModalOpen(true);
    }, []);

    const changeYear = useCallback((ev: ChangeEvent<HTMLSelectElement>): void => {
        if (ev.target.value !== undefined) {
            setLoading(true);
            if (yearDebounceRef.current) clearTimeout(yearDebounceRef.current);
            yearDebounceRef.current = setTimeout(() => {
                const year = ev.target.value;
                setStartYear(year === '' ? undefined : parseInt(year));
            }, 200);
        }
    }, []);

    return (
        <DashboardPageContent>
            <ShowParticipatingUsers
                event={eventData}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
            />

            <FormWrapper>
                <PageTitle>Edições anteriores</PageTitle>
                
                {oldEventList.length > 0 && (
                    <FiltersWrapper>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%" mt={8} mb={6}>
                            <Box>
                                <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                    Ano de início
                                </Text>
                                <Select
                                    bg="white"
                                    size="lg"
                                    fontSize="xl"
                                    h="50px"
                                    placeholder="Qualquer ano"
                                    onChange={changeYear}
                                    focusBorderColor="blue.400"
                                    shadow="sm"
                                    borderRadius="md"
                                >
                                    {eventListYear && eventListYear.length > 0 ? (
                                        eventListYear.map((y) => (
                                            <option key={y} value={y}>{y}</option>
                                        ))
                                    ) : (
                                        <option disabled>Sem Opções</option>
                                    )}
                                </Select>
                            </Box>
                            <Box>
                                <Text fontSize="xl" fontWeight="bold" mb={2} color="gray.700">
                                    Pesquisar Edições Anteriores
                                </Text>
                                
                                <Input
                                    size="lg"
                                    fontSize="xl"
                                    h="50px"
                                    bg="white"
                                    type="text"
                                    value={oldEventName}
                                    onChange={(e) => setOldEventName(e.target.value)}
                                    placeholder="Pesquise por nome ou sigla da área"
                                    focusBorderColor="blue.400"
                                    shadow="sm"
                                    borderRadius="md"
                                />
                            </Box>
                        </SimpleGrid>
                    </FiltersWrapper>
                )}

                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : filteredEvents && filteredEvents.length > 0 ? (
                    <EventsTable
                        events={filteredEvents}
                        path={path}
                        onDeleteClick={() => {
                            toast({
                                title: 'Ação não permitida',
                                description: 'Apenas administradores podem excluir edições.',
                                status: 'warning'
                            });
                        }}
                        onParticipantsClick={handleParticipantsClick}
                    />
                ) : loading ? (
                    <Flex justify="center" p={10}>
                        <LoadingSpinner />
                    </Flex>
                ) : (
                    <ErrorMessage>Nenhum resultado encontrado.</ErrorMessage>
                )}
            </FormWrapper>
        </DashboardPageContent>
    );
};

export default ListOldEvents;